<script setup>
import { ref, onMounted, watch, nextTick, onBeforeUnmount } from "vue";
import ArgonInput from "@/components/ArgonInput.vue";
import { useSlots } from 'vue';

const slots = useSlots();

const hasIconSlot = slots.icon !== undefined;
const emit = defineEmits([
  "update:modelValue",
  "textChanged",
  "selectedChange",
  "pageChanged",
  "closeMenu"
]);
const page=ref(1);
const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  menuWidth:{
    type: String,
    default: "200",
  },
  size: {
    type: String,
    default: "default",
  },
  error: {
    type: Boolean,
    default: false,
  },
  success: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Buscar...",
  },
  itemText: {
    type: Array,
    default() {
      return [];
    },
  },
  separator: {
    type: String,
    default: "-",
  },
  dataSuggestions: {
    type: Array,
    default() {
      return [];
    },
  },
  modelValue: {
    type: String,
    default: "",
  },
  init: {
    type: Number,
    default: 0,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  maxLength: {
    type: Number,
    default: 500,
  },
  iconSelected: {
    type: String,
    default: "",
  },
  withTooltip: {
    type: Boolean,
    default: false,
  },
});

const DEBOUNCE_MS = 300;
let debounceTimer = null;

var model = ref(props.modelValue);
var isVisible = ref(false);
const suggestionRefs = ref([]);
const index = ref(0);
const menuRef = ref(null);
var listSuggestions =ref(props.dataSuggestions);

function adjustAutocompletePosition() {
  const wrapper = document.getElementById(dynamicId.value);
  if (!wrapper || !menuRef.value) return;

  const rect = wrapper.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const menuWidth = menuRef.value.offsetWidth;

  menuRef.value.style.position = "fixed";
  menuRef.value.style.top = `${rect.bottom}px`;
  if (rect.left + menuWidth > viewportWidth) {
    menuRef.value.style.left = "auto";
    menuRef.value.style.right = `${viewportWidth - rect.right}px`;
  } else {
    menuRef.value.style.left = `${rect.left}px`;
    menuRef.value.style.right = "auto";
  }
}
const uniqueId = Math.random().toString(36).substr(2, 9);
const dynamicId=ref("");
onMounted(() => {
  dynamicId.value = `divRef-${uniqueId}`;
  model.value = props.modelValue;
  listSuggestions.value=props.dataSuggestions;
  window.addEventListener('resize', adjustAutocompletePosition);
  nextTick(() => {
    setIndent();
    if(props.iconSelected!=="")
      setIndent(true);
    const element = document.getElementById(dynamicId.value);
    if (element) {
      element.style.setProperty('--width', `${props.menuWidth}px`);
    }
  });
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', adjustAutocompletePosition);
  window.removeEventListener('scroll', adjustAutocompletePosition, true);
  if (menuRef.value) {
    menuRef.value.removeEventListener('scroll', handleScroll);
  }
  clearTimeout(debounceTimer);
});

function handleScroll() {
  const element = menuRef.value;
  if (!element) return;
  const isAtBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) <= 1;
  if (isAtBottom) {
    page.value++;
    emit('pageChanged',model.value,page.value);
  }
}

function setIndent(withIndent){
  let inputPoint=document.getElementById(props.id);
  if(withIndent){
    if(inputPoint && hasIconSlot)
      inputPoint.setAttribute("style", "text-indent: 25px;");
  }else{
    if(inputPoint)
      inputPoint.setAttribute("style", "text-indent: 0px;");
  }
}
function textView(item, itemText, separator) {
  var text = "";
  for (var i = 0; i < itemText.length; i++) {
    var value = getValueFromPath(item, itemText[i]) || "";
    if (value) {
      text = text + value;
      if (i < itemText.length - 1 && itemText.slice(i + 1).some(path => getValueFromPath(item, path))) {
        text = text + separator;
      }
    }
  }
  return text;
}
function matchSegments(text, query) {
  const trimmedQuery = String(query || "").trim();
  if (!trimmedQuery) return [{ text, matched: false }];

  const matchIndex = text.toLocaleLowerCase("es-ES").indexOf(trimmedQuery.toLocaleLowerCase("es-ES"));
  if (matchIndex === -1) return [{ text, matched: false }];

  return [
    { text: text.slice(0, matchIndex), matched: false },
    { text: text.slice(matchIndex, matchIndex + trimmedQuery.length), matched: true },
    { text: text.slice(matchIndex + trimmedQuery.length), matched: false },
  ].filter((segment) => segment.text !== "");
}
function getValueFromPath(obj, path) {
  const keys = path.split(".");
  let value = obj;

  for (let key of keys) {
    value = value[key];
    if (value === undefined) return undefined;
  }

  return value;
}
function resetKeyDownUpActions(){
  index.value= 0;
  isFirstKeydown= true;
}
function onFocus(){
  isVisible.value = true;
  listSuggestions.value = props.dataSuggestions;
  resetKeyDownUpActions();
  if (model.value && model.value.length < props.init) return;
  emit("textChanged", model.value, 1);
}
function textChanged(value) {
  model.value = value;
  if(model.value=="")
    setIndent(false);
  if (!value || value.length < props.init) {
    listSuggestions.value = [];
  }
  isVisible.value = true;
  emit("update:modelValue", value);
  page.value=1;
  clearTimeout(debounceTimer);
  if (value && value.length < props.init) {
    return;
  }
  debounceTimer = setTimeout(() => {
    emit("textChanged", model.value, page.value);
  }, DEBOUNCE_MS);
}
function clearInput(){
  clearTimeout(debounceTimer);
  model.value = "";
  emit("update:modelValue", "");
  page.value=1;
  emit("textChanged", model.value, page.value);
  nextTick(() => {
    const inputEl = document.getElementById(props.id);
    if (inputEl) inputEl.focus();
  });
}
function selectedChange(item) {
  setIndent(true)
  isVisible.value = false;
  model.value = textView(item, props.itemText, props.separator);
  emit("update:modelValue", model.value);
  emit("selectedChange", item);
}
function scrollIntoViewIfNeeded(element) {
  if(!element) return;
  const parent = element.parentNode;
  const parentRect = parent.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  if (elementRect.bottom > parentRect.bottom) {
    parent.scrollTop += elementRect.bottom - parentRect.bottom;
  } else if (elementRect.top < parentRect.top) {
    parent.scrollTop -= parentRect.top - elementRect.top;
  }
}
let isFirstKeydown = true;
function activateKeyScroll(index, container){
  if (!container){
    return
  }
  const itemHeight = container.querySelector(".item").offsetHeight;
  const  itemsShowed=5
  if (index >= itemsShowed) {
    container.scrollTop = (index - itemsShowed) * itemHeight;
  }
}
function handleKeydown(event) {
  const container = menuRef.value;
  const keyActions = {
    ArrowDown: () => {
      if(isFirstKeydown) isFirstKeydown= false
      else if (index.value < suggestionRefs.value.length - 1){
        index.value++;
        activateKeyScroll(index.value, container);
      } 
    },
    ArrowUp: () => {
      if (index.value > 0){
        index.value--;
        activateKeyScroll(index.value, container);
      }
    },
    Enter: () => {
      if (index.value >= 0) suggestionRefs.value[index.value]?.click();
    },
    Escape: () => {
      isVisible.value=false;
    },
    Tab: () => {
      isVisible.value=false;
    }
  };
  const action = keyActions[event.key];
  if (action) {
    if (event.key !== "Backspace" && event.key !== "Tab") {
      event.preventDefault();
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      suggestionRefs.value[index.value]?.classList.remove("focused");
    }
    action();
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      suggestionRefs.value[index.value]?.classList.add("focused");
      scrollIntoViewIfNeeded(suggestionRefs.value[index.value]);
    }
  }
}
function handleClickOutside(){
  listSuggestions.value=[];
  emit("closeMenu")
  isVisible.value=false;
}
watch(
  () => props.modelValue,
  (val) => {
    model.value = val;
  }
);
watch(model, (newVal) => {
  if (newVal && suggestionRefs.value.length > 0) {
    index.value = 0;
    suggestionRefs.value[index.value].classList.add("focused");
    scrollIntoViewIfNeeded(suggestionRefs.value[index.value]);
  }
});
watch(() => props.iconSelected, (value) => {
  if(value!==""){
    setIndent(true);
  }else setIndent(false);
});
watch(() => props.dataSuggestions, (value) => {
  listSuggestions.value=value;
});

watch(() => isVisible.value, (value) => {
  if(!value){
    page.value=1;
  }
});

watch(() => menuRef.value, (value) => {
  if (value !== null) {
    nextTick(() => {
      if (menuRef.value) {
        menuRef.value.addEventListener('scroll', handleScroll);
      }
      adjustAutocompletePosition();
      window.addEventListener('scroll', adjustAutocompletePosition, true);
    });
  } else {
    window.removeEventListener('scroll', adjustAutocompletePosition, true);
  }
});
</script>
<template>
  <div :id="dynamicId" v-click-outside="handleClickOutside" class="col mb-0 p-0 border-0 dropdown auto-complete" :class="{ visible: isVisible && listSuggestions.length > 0}">
    <img v-if="iconSelected!='' && model!='' " :src="iconSelected" class="input-icon"  />
    <argon-input
      :id="id"
      v-model="model"
      :label="label"
      :max-length="maxLength"
      :is-required="true"
      :placeholder="placeholder"
      :name="name"
      :error="error"
      :size="size"
      :disabled="disabled"
      autocomplete="off"
      class="mb-0 input-dropdown-menu"
      @input="textChanged($event.target.value)"
      @keydown="handleKeydown"
      @focus="onFocus"
    />
    <span v-if="model && model !== '' && !disabled" class="clear-btn" role="button" aria-label="Limpiar" @mousedown.prevent @click="clearInput"></span>
    <div
      v-if="isVisible && listSuggestions.length > 0"
      :id="`menu-${uniqueId}`"
      ref="menuRef"
      class="searcher-menu-auto-complete searcher-menu-config shadow rounded mt-3 p-1"
      aria-labelledby="searcherTextDropdown"
    >
      <div v-for="(item, key) in listSuggestions" :key="key" class="d-flex align-items-center item">
        <slot name="icon" :item="item"></slot>
        <a
          ref="suggestionRefs"
          v-tooltip="withTooltip?textView(item, itemText, separator):null"
          class="dropdown-item border-radius-md flex-grow-1 text-truncate"
          @click="selectedChange(item)"
          ><span class="ellipsis"
            ><span
              v-for="(segment, segmentIndex) in matchSegments(textView(item, itemText, separator), model)"
              :key="segmentIndex"
              :class="{ 'match-highlight': segment.matched }"
              >{{ segment.text }}</span
            ></span
          ></a
        >
        <slot name="action" :item="item"></slot>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.input-icon {
  position: absolute;
  left: 0px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
}
.searcher-menu-config {
  max-height: 200px;
  overflow-y: auto;
  opacity: 1;
  min-width: var(--width);
  max-width: var(--width);
}
.auto-complete {
  position: relative;
}
.focused {
  color: #495057;
  background-color: #e9ecef;
}
.ellipsis {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.match-highlight {
  font-weight: 700;
}

.text-truncate {
  max-width: 100%;
}


.auto-complete {
  &::before {
    font: normal normal normal 14px/1 FontAwesome;
    content: "\f0d8";
    font-size: 22px;
    color: #ffffff;
    position: absolute;
    top: 27px;
    left: 20px;
    transition: top 0.35s ease;
    visibility: hidden;
    opacity: 0;
  }
  
  &.visible::before {
    visibility: visible;
    opacity: 1;
  }
  .clear-btn {
    background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjEiIGhlaWdodD0iMjEiIHZpZXdCb3g9IjAgMCAyMSAyMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSIjMDAwIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yLjU5Mi4wNDRsMTguMzY0IDE4LjM2NC0yLjU0OCAyLjU0OEwuMDQ0IDIuNTkyeiIvPjxwYXRoIGQ9Ik0wIDE4LjM2NEwxOC4zNjQgMGwyLjU0OCAyLjU0OEwyLjU0OCAyMC45MTJ6Ii8+PC9nPjwvc3ZnPg==);
    background-repeat: no-repeat;
    background-position: center;
    background-size: 8px;
    position: absolute;
    top: 50%;
    right: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: inline-block;
    cursor: pointer;
    visibility: hidden;
    opacity: 0.5;
    transition: opacity 0.15s ease, visibility 0.15s ease;
    transform: translateY(-50%);
    &:hover {
      opacity: 1;
    }
  }
  &:hover .clear-btn {
    visibility: visible;
  }
}
</style>